import { DONATION_TYPES, PAYMENT_METHODS, FREQUENCIES, SUBSCRIPTION_END_TYPES } from './groceryModels';

const NAME_PATTERN = /^[A-Za-z\s'.-]+$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// US ZIP: 5 digits, optionally followed by the +4 extension
const US_ZIP_PATTERN = /^\d{5}(-\d{4})?$/;

export const validateDonor = (donor) => {
  const errors = {};

  if (!donor.firstName?.trim()) {
    errors.firstName = 'First name is required';
  } else if (!NAME_PATTERN.test(donor.firstName.trim())) {
    errors.firstName = 'Letters only';
  }

  if (!donor.lastName?.trim()) {
    errors.lastName = 'Last name is required';
  } else if (!NAME_PATTERN.test(donor.lastName.trim())) {
    errors.lastName = 'Letters only';
  }

  if (!donor.email?.trim()) {
    errors.email = 'Email is required';
  } else if (!EMAIL_PATTERN.test(donor.email.trim())) {
    errors.email = 'Enter a valid email address';
  }

  const phoneDigits = (donor.phone || '').replace(/\D/g, '');
  if (!phoneDigits) {
    errors.phone = 'Phone number is required';
  } else if (phoneDigits.length !== 10) {
    errors.phone = 'Enter a 10-digit phone number';
  }

  return errors;
};

export const validatePaymentStep = ({
  donationType,
  paymentMethod,
  frequency,
  card,
  ach,
  recurring,
  dropOff,
  recaptchaToken,
}) => {
  const errors = {};

  if (!recaptchaToken) {
    errors.recaptcha = 'Please complete the reCAPTCHA verification';
  }

  if (donationType === DONATION_TYPES.PHYSICAL) {
    if (!dropOff?.date) errors.dropOffDate = 'Please choose a drop-off date';
    if (!dropOff?.time) errors.dropOffTime = 'Please choose a drop-off time';
    return errors;
  }

  if (!paymentMethod) {
    errors.paymentMethod = 'Please select a payment method';
    return errors;
  }

  if (paymentMethod === PAYMENT_METHODS.CARD) {
    const cardDigits = (card?.cardNumber || '').replace(/\D/g, '');
    if (!cardDigits) {
      errors.cardNumber = 'Card number is required';
    } else if (cardDigits.length < 13 || cardDigits.length > 16) {
      errors.cardNumber = 'Enter a valid card number';
    }

    if (!card?.expiryDate) {
      errors.expiryDate = 'Expiry date is required';
    } else {
      const match = /^(\d{2})\/(\d{2})$/.exec(card.expiryDate);
      if (!match) {
        errors.expiryDate = 'Use MM/YY format';
      } else {
        const month = parseInt(match[1], 10);
        const year = 2000 + parseInt(match[2], 10);
        const now = new Date();
        const endOfMonth = new Date(year, month, 0, 23, 59, 59);
        if (month < 1 || month > 12) {
          errors.expiryDate = 'Invalid month (use 01-12)';
        } else if (endOfMonth < now) {
          errors.expiryDate = 'Card has expired';
        } else if (year > now.getFullYear() + 20) {
          errors.expiryDate = 'Expiry date is too far in the future';
        }
      }
    }

    const rawCvv = card?.cvv || '';
    const cvv = rawCvv.replace(/\D/g, '');
    if (!cvv) {
      errors.cvv = 'CVV is required';
    } else if (cvv.length !== rawCvv.trim().length) {
      errors.cvv = 'CVV must contain digits only';
    } else if (cvv.length < 3 || cvv.length > 4) {
      errors.cvv = 'CVV must be 3 or 4 digits';
    }

    const zip = (card?.billingZipCode || '').trim();
    if (!zip) {
      errors.billingZipCode = 'Billing ZIP is required';
    } else if (!US_ZIP_PATTERN.test(zip)) {
      errors.billingZipCode = 'Enter a valid US ZIP (12345 or 12345-6789)';
    }
  }

  if (paymentMethod === PAYMENT_METHODS.ACH) {
    const routing = (ach?.routingNumber || '').replace(/\D/g, '');
    if (!routing) {
      errors.routingNumber = 'Routing number is required';
    } else if (routing.length !== 9) {
      errors.routingNumber = 'Routing number must be 9 digits';
    }

    if (!ach?.accountNumber?.trim()) {
      errors.accountNumber = 'Account number is required';
    }

    if (!ach?.bankName?.trim()) {
      errors.bankName = 'Bank name is required';
    }
  }

  if (frequency === FREQUENCIES.RECURRING) {
    if (!recurring?.startDate) {
      errors.recurringStartDate = 'Start date is required';
    }

    if (recurring?.endType === SUBSCRIPTION_END_TYPES.ON_DATE) {
      if (!recurring?.endDate) {
        errors.recurringEndDate = 'End date is required';
      } else if (recurring?.startDate && recurring.endDate <= recurring.startDate) {
        errors.recurringEndDate = 'End date must be after the start date';
      }
    }

    if (recurring?.endType === SUBSCRIPTION_END_TYPES.AFTER_TERMS) {
      const terms = parseInt(recurring?.numberOfTerms || '', 10);
      if (!terms || terms < 1) {
        errors.numberOfTerms = 'Enter the number of payments';
      }
    }
  }

  return errors;
};

export const hasErrors = (errors) => Object.keys(errors || {}).length > 0;
